/**
 * Menu v3 rollout gating (spec: 2026-07-03-replenishment-redesign-core-design.md;
 * new-user rule revised 2026-07-06 per user decision).
 * Precedence: atlasV3 override > explicit user choice > legacy v1 > env all-users
 * flag > new-user merchant-id cutoff > classic default.
 *
 * NOTE: legacy 'menuVersion' localStorage was auto-persisted by
 * initializeMenuVersion for every store, so a stored '2' is NOT an explicit
 * choice and must not block the env-flag rollout. Only '1' is honored
 * (pre-v2 stores). Explicit choices live in 'menuVersionChoice'.
 *
 * Package-independent as of phase 2: v3 rollout applies to Free/Pro stores
 * the same as Replenishment-package stores (selectMenuConfig picks the
 * right v3 menu shape per tier). `isReplenishmentPackage` is kept on
 * MenuRolloutInput because VerticalMenuBar.vue still builds/passes it, but
 * it is intentionally unused here.
 */

/**
 * New-user cutoff (user decision, 2026-07-06): merchants with id >= 17010
 * default to the new v3 menu; earlier merchants keep the classic v2 default.
 * Merchant ids are assigned sequentially at install, so the id is the
 * authoritative "new user" signal (replaces the earlier installedAt-date
 * heuristic, which depended on a client-parseable timestamp).
 *
 * Overridable per environment via VUE_APP_ATLAS_V3_MIN_MERCHANT_ID (baked at
 * build time, like VUE_APP_ATLAS_V3_ALL_USERS); this constant is the fail-safe
 * fallback when the env var is unset or not a positive integer.
 */
export const ATLAS_V3_NEW_USER_MIN_MERCHANT_ID = 17010;

function newUserMinMerchantId(): number {
  const raw = process.env.VUE_APP_ATLAS_V3_MIN_MERCHANT_ID;
  const parsed = raw != null ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : ATLAS_V3_NEW_USER_MIN_MERCHANT_ID;
}

export interface MenuRolloutInput {
  merchantId: number | null | undefined;
  isReplenishmentPackage: boolean;
}

export type MenuVersion = 1 | 2 | 3;

export function resolveDefaultMenuVersion(input: MenuRolloutInput): MenuVersion {
  const override = localStorage.getItem('atlasV3');
  if (override === 'true') return 3;
  if (override === 'false') return 2;

  const choice = localStorage.getItem('menuVersionChoice');
  if (choice === '1' || choice === '2' || choice === '3') {
    return parseInt(choice, 10) as MenuVersion;
  }

  if (localStorage.getItem('menuVersion') === '1') return 1;

  if (process.env.VUE_APP_ATLAS_V3_ALL_USERS === 'true') return 3;

  // A missing/invalid merchant id is treated as an EXISTING user (v2), never
  // a fake new user — same fail-safe direction as the rest of the rollout.
  if (
    typeof input.merchantId === 'number'
    && Number.isFinite(input.merchantId)
    && input.merchantId >= newUserMinMerchantId()
  ) {
    return 3;
  }
  return 2;
}
